const ATTACHMENT_ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.ms-outlook',
  'application/octet-stream',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]);

const ATTACHMENT_ALLOWED_EXTENSIONS = ['.csv', '.pdf', '.docx', '.doc', '.msg', '.xlsx', '.xls'];

const GENERIC_CLIPBOARD_NAME_PATTERN =
  /^(image|screenshot|clipboard|paste-image)([-_\s].*)?\.(png|jpe?g|gif|webp|bmp)$/i;

const IMAGE_SUBTYPE_EXTENSION_MAP: Record<string, string> = {
  jpeg: 'jpg',
  pjpeg: 'jpg',
  'svg+xml': 'svg',
  'vnd.microsoft.icon': 'ico'
};

export const ATTACHMENT_ACCEPT_TYPES = [
  'image/*',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  '.msg',
  '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
] as const;

function inferExtension(file: File): string {
  if (file.type.startsWith('image/')) {
    const subtype = String(file.type.split('/')[1] || '')
      .trim()
      .toLowerCase();
    const baseSubtype = subtype.split('+')[0] || '';

    return IMAGE_SUBTYPE_EXTENSION_MAP[subtype] || IMAGE_SUBTYPE_EXTENSION_MAP[baseSubtype] || baseSubtype || 'png';
  }

  const name = String(file.name || '')
    .trim()
    .toLowerCase();
  const matchedExtension = ATTACHMENT_ALLOWED_EXTENSIONS.find((extension) => name.endsWith(extension));
  return matchedExtension ? matchedExtension.replace('.', '') : 'bin';
}

export function normalizeAttachmentFiles(files: File[] | FileList, prefix = 'screenshot'): File[] {
  const timestamp = Date.now();

  return Array.from(files).map((file, index) => {
    const fileName = String(file.name || '').trim();
    const needsGeneratedName = !fileName || GENERIC_CLIPBOARD_NAME_PATTERN.test(fileName);
    if (!needsGeneratedName) return file;

    const extension = inferExtension(file);
    return new File([file], `${prefix}-${timestamp}-${index + 1}.${extension}`, {
      type: file.type || 'application/octet-stream'
    });
  });
}

export function isSupportedAttachment(file: File): boolean {
  const mime = String(file.type || '')
    .trim()
    .toLowerCase();
  if (mime.startsWith('image/')) return true;
  if (ATTACHMENT_ALLOWED_MIME_TYPES.has(mime)) return true;

  const lowerName = String(file.name || '').toLowerCase();
  return ATTACHMENT_ALLOWED_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
}
